import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import Icon from "../HOC/Icon";
import getIssues from "../../controllers/logger/getIssues";
import LoggerBadge from "./LoggerBadge";
import {
  environmentTone,
  orderLabel,
  statusCodeTone,
  statusTone,
  truncate,
} from "./loggerUtils";

export default function LoggerGate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const res = await getIssues({
        page: 1,
        limit: 5,
        status: "unresolved",
        environment: "production",
      });
      if (!active) return;
      setLoading(false);
      if (res.return) {
        setIssues(res.data || []);
        setMeta(res.meta || { total: 0 });
        setError("");
      } else {
        setIssues([]);
        setError(res.msg || "Unable to load issue logs");
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <section className="w-full mt-2">
      <div className="rounded-2xl overflow-hidden border border-[#D6E4FF] bg-white shadow-[0_12px_40px_rgba(30,97,220,0.08)]">
        <div className="relative overflow-hidden bg-gradient-to-br from-[#EEF5FF] via-[#F7FAFF] to-white px-4 py-5 sm:px-6 sm:py-6">
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#1E61DC]/10 blur-2xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/3 h-24 w-24 rounded-full bg-[#80EFDF]/30 blur-2xl" />

          <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#1E61DC]/10 text-primary1">
                <Icon icon="solar:bug-minimalistic-bold-duotone" className="!h-7 !w-7" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg sm:text-xl font-bold text-[#0F172A]">
                    API Issue Logger
                  </h3>
                  <LoggerBadge bg="#DBEAFE" color="#1D4ED8">
                    Internal
                  </LoggerBadge>
                </div>
                <p className="mt-1 text-sm text-[#475569] max-w-xl">
                  Production failures captured for debugging. Open the log board to
                  search, filter, resolve, and extend retention.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate("/logs")}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary1 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-[#1E61DC]/25 transition hover:brightness-110 hover:-translate-y-0.5"
            >
              Open logs
              <Icon icon="weui:arrow-filled" className="!h-4 !w-4" />
            </button>
          </div>

          <div className="relative z-10 mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatPill
              label="Unresolved (prod)"
              value={loading ? "…" : meta.total ?? issues.length}
              tone="warn"
            />
            <StatPill
              label="Showing"
              value={loading ? "…" : Math.min(5, issues.length)}
              tone="info"
            />
            <StatPill
              label="Retention"
              value="30 days"
              tone="ok"
              className="col-span-2 sm:col-span-1"
            />
          </div>
        </div>

        <div className="border-t border-[#E8EEF8] bg-white">
          <div className="flex items-center justify-between px-4 py-3 sm:px-6">
            <span className="text-sm font-semibold text-[#0F172A]">
              Latest unresolved production issues
            </span>
            <Link
              to="/logs"
              className="text-sm font-medium text-primary1 hover:underline inline-flex items-center gap-1"
            >
              View all
              <Icon icon="weui:arrow-filled" className="!h-3.5 !w-3.5" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <CircularProgress size={28} />
            </div>
          ) : error ? (
            <div className="mx-4 mb-4 sm:mx-6 rounded-xl border border-[#FED7AA] bg-[#FFF7ED] px-4 py-3 text-sm text-[#9A3412]">
              {error}
            </div>
          ) : issues.length === 0 ? (
            <div className="px-4 pb-6 sm:px-6 text-sm text-[#64748B]">
              No unresolved production issues right now. Nice.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                    <tr>
                      <th className="px-6 py-3 font-semibold">When</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Endpoint</th>
                      <th className="px-4 py-3 font-semibold">Page</th>
                      <th className="px-4 py-3 font-semibold">Order</th>
                      <th className="px-6 py-3 font-semibold text-right">Env</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EEF2F7]">
                    {issues.map((issue) => {
                      const code = statusCodeTone(issue.statusCode);
                      const env = environmentTone(issue.environment);
                      const st = statusTone(issue.status);
                      return (
                        <tr
                          key={issue._id}
                          onClick={() => navigate(`/logs?id=${issue._id}`)}
                          className="cursor-pointer transition hover:bg-[#F8FBFF]"
                        >
                          <td className="px-6 py-3 whitespace-nowrap text-[#475569]">
                            {moment(issue.createdAt).fromNow()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1.5">
                              <LoggerBadge {...code}>{issue.statusCode}</LoggerBadge>
                              <LoggerBadge {...st}>{st.label}</LoggerBadge>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-[#0F172A]">
                              <span className="text-[#64748B] mr-1">{issue.method}</span>
                              {truncate(issue.endpointCalled, 36)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[#334155]">
                            {truncate(issue.page, 28)}
                          </td>
                          <td className="px-4 py-3 text-[#334155]">
                            {orderLabel(issue.order)}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <LoggerBadge {...env}>{env.label}</LoggerBadge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden flex flex-col gap-3 px-4 pb-4">
                {issues.map((issue) => {
                  const code = statusCodeTone(issue.statusCode);
                  const env = environmentTone(issue.environment);
                  return (
                    <button
                      key={issue._id}
                      type="button"
                      onClick={() => navigate(`/logs?id=${issue._id}`)}
                      className="rounded-xl border border-[#E8EEF8] bg-[#F8FAFC] p-3 text-left transition active:scale-[0.99]"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex flex-wrap gap-1.5">
                          <LoggerBadge {...code}>{issue.statusCode}</LoggerBadge>
                          <LoggerBadge {...env}>{env.label}</LoggerBadge>
                        </div>
                        <span className="text-xs text-[#64748B] whitespace-nowrap">
                          {moment(issue.createdAt).fromNow()}
                        </span>
                      </div>
                      <div className="mt-2 text-sm font-semibold text-[#0F172A]">
                        {issue.method} {truncate(issue.endpointCalled, 40)}
                      </div>
                      <div className="mt-1 text-xs text-[#64748B]">
                        {issue.page} · {orderLabel(issue.order)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function StatPill({ label, value, tone = "info", className = "" }) {
  const tones = {
    warn: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
    info: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
    ok: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
  };
  return (
    <div
      className={`rounded-xl border px-3 py-2.5 ${tones[tone]} ${className}`}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide opacity-80">
        {label}
      </div>
      <div className="mt-0.5 text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}
